/**
 * Interaction Runtime Warnings — Wave 22.13.2
 *
 * Detects and surfaces diagnostic warnings for the Plugin Interaction
 * Inspector. These warnings help developers identify common integration
 * issues without requiring manual debugging.
 *
 * Warning conditions:
 *   1. `ui.openModal` called but no modal component registered for viewId
 *   2. Modal pushed but `PluginModalHost` is not mounted
 *   3. Deferred action pending > 10 seconds without resolution
 *   4. Plugin emits event but no subscriber for target pluginId
 *   5. Plugin has interactive actions but no setup() function
 *   6. Plugin uses legacy direct-export API pattern
 *   7. Plugin setup() received APIs but verification failed
 *   8. Interactive action invoked but ui.openModal never called (Wave 22.13.2)
 */

import { useModalStackStore } from './modal-stack-manager';
import { useDeferredActionStore } from './deferred-action-registry';
import { usePluginEventBusStore } from './plugin-event-bus';
import { getRegisteredModalViewIds } from '../components/PluginModalHost';
import type { ActiveModalSnapshot, DeferredAction, UIInvocationLogEntry } from '../types/plugin-interaction';

// ────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────

export type InteractionWarningSeverity = 'error' | 'warning' | 'info';

export interface InteractionWarning {
  id: string;
  severity: InteractionWarningSeverity;
  code: string;
  message: string;
  pluginId?: string;
  detail: Record<string, any>;
  timestamp: string;
}

// ────────────────────────────────────────────────────────
// PluginModalHost mount tracking
// ────────────────────────────────────────────────────────

let _pluginModalHostMounted = false;

/**
 * Call from PluginModalHost on mount/unmount to track its presence.
 * This allows warnings when modals are pushed but no host is rendering them.
 */
export function setPluginModalHostMounted(mounted: boolean): void {
  _pluginModalHostMounted = mounted;
}

/** Check if PluginModalHost is currently mounted. */
export function isPluginModalHostMounted(): boolean {
  return _pluginModalHostMounted;
}

// ────────────────────────────────────────────────────────
// Plugin sandbox API binding tracking
// ────────────────────────────────────────────────────────

export interface PluginAPIBindingRecord {
  pluginId: string;
  apis: {
    ui: boolean;
    actions: boolean;
    events: boolean;
    contentSync: boolean;
  };
  boundAt: string;
}

const _pluginAPIBindings = new Map<string, PluginAPIBindingRecord>();

/**
 * Record that APIs were injected into a plugin.
 * Called by createPluginInteractionAPIs or individual factories.
 */
export function recordAPIBinding(
  pluginId: string,
  apis: { ui?: boolean; actions?: boolean; events?: boolean; contentSync?: boolean },
): void {
  const existing = _pluginAPIBindings.get(pluginId);
  _pluginAPIBindings.set(pluginId, {
    pluginId,
    apis: {
      ui: apis.ui ?? existing?.apis.ui ?? false,
      actions: apis.actions ?? existing?.apis.actions ?? false,
      events: apis.events ?? existing?.apis.events ?? false,
      contentSync: apis.contentSync ?? existing?.apis.contentSync ?? false,
    },
    boundAt: existing?.boundAt ?? new Date().toISOString(),
  });
}

/** Get all recorded API bindings for DevTools. */
export function getPluginAPIBindings(): PluginAPIBindingRecord[] {
  return Array.from(_pluginAPIBindings.values());
}

/** Clear all recorded bindings (for testing). */
export function clearPluginAPIBindings(): void {
  _pluginAPIBindings.clear();
}

// ────────────────────────────────────────────────────────
// Wave 22.13.2: Plugin Setup Verification Tracking
// ────────────────────────────────────────────────────────

export interface PluginSetupVerification {
  pluginId: string;
  hasSetup: boolean;
  apisAvailable: {
    ui: boolean;
    actions: boolean;
    events: boolean;
    contentSync?: boolean;
  };
  registerModalComponentAvailable: boolean;
  setupError?: string;
  missingSetupWithInteractiveActions?: boolean;
  timestamp: string;
}

const _pluginSetupVerifications = new Map<string, PluginSetupVerification>();

/**
 * Record verification result after a plugin's setup() is called.
 * Used by PluginLoader to track whether the host APIs were properly injected
 * and whether the plugin actually consumed them.
 */
export function recordPluginSetupVerification(
  pluginId: string,
  verification: PluginSetupVerification,
): void {
  _pluginSetupVerifications.set(pluginId, verification);
}

/** Get all recorded setup verifications for DevTools. */
export function getPluginSetupVerifications(): PluginSetupVerification[] {
  return Array.from(_pluginSetupVerifications.values());
}

/** Clear all recorded verifications (for testing). */
export function clearPluginSetupVerifications(): void {
  _pluginSetupVerifications.clear();
}

// ────────────────────────────────────────────────────────
// Wave 22.13.2: Legacy Direct-Export API Detection
// ────────────────────────────────────────────────────────

export interface LegacyAPIDetection {
  pluginId: string;
  legacyExports: { ui?: boolean; actions?: boolean; events?: boolean };
  detectedAt: number;
}

const _legacyAPIDetections = new Map<string, LegacyAPIDetection>();

export function recordLegacyAPIDetection(detection: LegacyAPIDetection): void {
  _legacyAPIDetections.set(detection.pluginId, detection);
}

export function getLegacyAPIDetections(): LegacyAPIDetection[] {
  return Array.from(_legacyAPIDetections.values());
}

export function clearLegacyAPIDetections(): void {
  _legacyAPIDetections.clear();
}

// ────────────────────────────────────────────────────────
// Wave 22.13.2: Interactive Action Invocation Tracking
// ────────────────────────────────────────────────────────

/**
 * Tracks interactive action invocations to detect when an action
 * handler is triggered but never calls ui.openModal().
 *
 * Flow:
 *   1. When an action handler runs, plugin-ui-extensions.ts records
 *      the invocation via `recordInteractiveActionInvocation()`.
 *   2. When `ui.openModal()` is called, `markOpenModalCalled()` clears
 *      the pending entry for that plugin.
 *   3. `computeInteractionWarnings()` checks for stale entries older
 *      than `INTERACTIVE_NO_MODAL_THRESHOLD_MS` — these indicate the
 *      action ran but never opened a modal.
 */

const INTERACTIVE_NO_MODAL_THRESHOLD_MS = 2_000;

export interface InteractiveActionInvocationRecord {
  pluginId: string;
  viewId?: string;
  invokedAt: number;       // Date.now()
  openModalCalled: boolean;
  handlerError?: string;
}

const _interactiveActionInvocations = new Map<string, InteractiveActionInvocationRecord>();

/**
 * Record that a plugin's interactive action was invoked.
 * Called by the traced `ui.openModal()` wrapper in plugin-ui-extensions.ts.
 *
 * This records the intent to open a modal — if `markOpenModalCalled()`
 * is never subsequently called for the same pluginId, the warning fires.
 */
export function recordInteractiveActionInvocation(
  pluginId: string,
  viewId?: string,
): void {
  _interactiveActionInvocations.set(pluginId, {
    pluginId,
    viewId,
    invokedAt: Date.now(),
    openModalCalled: true, // Called from ui.openModal — mark as satisfied
  });
}

/**
 * Record that an action handler was triggered for a plugin.
 * Called from the traced action register wrapper in host-ui-api.ts.
 * This starts the "clock" — if openModal is not called within the
 * threshold, the INTERACTIVE_ACTION_NO_MODAL warning fires.
 */
export function recordActionHandlerTriggered(
  pluginId: string,
  actionId?: string,
): void {
  _interactiveActionInvocations.set(`${pluginId}::${actionId ?? 'unknown'}`, {
    pluginId,
    viewId: actionId,
    invokedAt: Date.now(),
    openModalCalled: false,
  });
}

/**
 * Mark that ui.openModal was called for a plugin, satisfying the
 * INTERACTIVE_ACTION_NO_MODAL condition.
 */
export function markOpenModalCalled(pluginId: string): void {
  for (const [key, record] of _interactiveActionInvocations.entries()) {
    if (record.pluginId === pluginId && !record.openModalCalled) {
      record.openModalCalled = true;
    }
  }
}

/**
 * Record that an action handler threw an error before calling openModal.
 */
export function recordActionHandlerError(
  pluginId: string,
  actionId: string,
  error: string,
): void {
  const key = `${pluginId}::${actionId}`;
  const existing = _interactiveActionInvocations.get(key);
  if (existing) {
    existing.handlerError = error;
  }
}

/** Get all interactive action invocations for DevTools. */
export function getInteractiveActionInvocations(): InteractiveActionInvocationRecord[] {
  return Array.from(_interactiveActionInvocations.values());
}

/** Clear all interactive action invocations (for testing). */
export function clearInteractiveActionInvocations(): void {
  _interactiveActionInvocations.clear();
}

// ────────────────────────────────────────────────────────
// Warning ID generation
// ────────────────────────────────────────────────────────

let _nextWarnId = 1;

function warnId(): string {
  return `warn-${Date.now()}-${_nextWarnId++}`;
}

// ────────────────────────────────────────────────────────
// Warning detection
// ────────────────────────────────────────────────────────

const PENDING_ACTION_THRESHOLD_MS = 10_000;

/**
 * Compute all current interaction warnings.
 *
 * This is a pure snapshot function — it reads current state from
 * all stores and returns warnings. The inspector polls this on
 * each render cycle.
 */
export function computeInteractionWarnings(): InteractionWarning[] {
  const warnings: InteractionWarning[] = [];
  const now = Date.now();

  // ── 1. Missing modal components ──
  const registeredViewIds = new Set(getRegisteredModalViewIds());
  const modalSnapshots = useModalStackStore.getState().getSnapshots();

  for (const modal of modalSnapshots) {
    if (!registeredViewIds.has(modal.viewId)) {
      warnings.push({
        id: warnId(),
        severity: 'error',
        code: 'MODAL_COMPONENT_MISSING',
        message: `ui.openModal called with viewId="${modal.viewId}" but no modal component is registered for this viewId.`,
        pluginId: modal.pluginId,
        detail: { viewId: modal.viewId, modalId: modal.id },
        timestamp: modal.openedAt,
      });
    }
  }

  // ── 2. PluginModalHost not mounted ──
  if (modalSnapshots.length > 0 && !_pluginModalHostMounted) {
    warnings.push({
      id: warnId(),
      severity: 'error',
      code: 'MODAL_HOST_NOT_MOUNTED',
      message: `${modalSnapshots.length} modal(s) in stack but PluginModalHost is not mounted. Modals will not render.`,
      detail: { stackSize: modalSnapshots.length },
      timestamp: new Date().toISOString(),
    });
  }

  // ── 3. Long-pending deferred actions ──
  const allActions = useDeferredActionStore.getState().getAll();
  for (const action of allActions) {
    if (action.status === 'pending') {
      const createdMs = new Date(action.createdAt).getTime();
      const elapsed = now - createdMs;
      if (elapsed > PENDING_ACTION_THRESHOLD_MS) {
        warnings.push({
          id: warnId(),
          severity: 'warning',
          code: 'DEFERRED_ACTION_STALE',
          message: `Deferred action "${action.label || action.actionId}" has been pending for ${Math.round(elapsed / 1000)}s (threshold: ${PENDING_ACTION_THRESHOLD_MS / 1000}s).`,
          pluginId: action.pluginId,
          detail: { actionId: action.actionId, elapsedMs: elapsed },
          timestamp: action.createdAt,
        });
      }
    }
  }

  // ── 4. Missing API bindings ──
  for (const binding of _pluginAPIBindings.values()) {
    const missing: string[] = [];
    if (!binding.apis.ui) missing.push('ui');
    if (!binding.apis.actions) missing.push('actions');
    if (!binding.apis.events) missing.push('events');

    if (missing.length > 0) {
      warnings.push({
        id: warnId(),
        severity: 'info',
        code: 'API_BINDING_INCOMPLETE',
        message: `Plugin "${binding.pluginId}" is missing API bindings: ${missing.join(', ')}.`,
        pluginId: binding.pluginId,
        detail: { missing, apis: binding.apis },
        timestamp: binding.boundAt,
      });
    }
  }

  // ── 5. Deferred action with no modal opened ──
  // If a deferred action is pending but no modal is in the stack and
  // the action has been pending for > 3 seconds, warn that a modal
  // may need to be opened to resolve it.
  if (modalSnapshots.length === 0) {
    for (const action of allActions) {
      if (action.status === 'pending') {
        const createdMs = new Date(action.createdAt).getTime();
        const elapsed = now - createdMs;
        if (elapsed > 3_000) {
          warnings.push({
            id: warnId(),
            severity: 'warning',
            code: 'DEFERRED_NO_MODAL',
            message: `Deferred action "${action.label || action.actionId}" is pending but no modal is open. ` +
              `If this action depends on user input via a modal, ensure ui.openModal() is called.`,
            pluginId: action.pluginId,
            detail: { actionId: action.actionId, elapsedMs: elapsed },
            timestamp: action.createdAt,
          });
        }
      }
    }
  }

  // ── 6. Wave 22.13.2: Missing setup() with interactive actions ──
  for (const verification of _pluginSetupVerifications.values()) {
    if (verification.missingSetupWithInteractiveActions) {
      warnings.push({
        id: warnId(),
        severity: 'error',
        code: 'PLUGIN_SETUP_MISSING',
        message: `Plugin "${verification.pluginId}" declares interactive actions (interaction: 'modal') but has no setup() function. ` +
          `Actions requiring host UI modals will not function. Add a setup(context) function that calls registerEditMarkdownAction().`,
        pluginId: verification.pluginId,
        detail: { verification },
        timestamp: verification.timestamp,
      });
    }

    // Warn if setup() threw an error
    if (verification.setupError) {
      warnings.push({
        id: warnId(),
        severity: 'error',
        code: 'PLUGIN_SETUP_ERROR',
        message: `Plugin "${verification.pluginId}" setup() threw an error: ${verification.setupError}`,
        pluginId: verification.pluginId,
        detail: { error: verification.setupError },
        timestamp: verification.timestamp,
      });
    }

    // Info-level: APIs were injected but some weren't available (rare)
    if (verification.hasSetup && !verification.setupError) {
      const missingApis: string[] = [];
      if (!verification.apisAvailable.ui) missingApis.push('ui');
      if (!verification.apisAvailable.actions) missingApis.push('actions');
      if (!verification.apisAvailable.events) missingApis.push('events');
      if (missingApis.length > 0) {
        warnings.push({
          id: warnId(),
          severity: 'warning',
          code: 'PLUGIN_API_VERIFICATION_FAILED',
          message: `Plugin "${verification.pluginId}" setup() was called but API verification failed for: ${missingApis.join(', ')}.`,
          pluginId: verification.pluginId,
          detail: { missingApis, apisAvailable: verification.apisAvailable },
          timestamp: verification.timestamp,
        });
      }
    }
  }

  // ── 7. Wave 22.13.2: Legacy direct-export API pattern ──
  for (const detection of _legacyAPIDetections.values()) {
    const exports: string[] = [];
    if (detection.legacyExports.ui) exports.push('ui');
    if (detection.legacyExports.actions) exports.push('actions');
    if (detection.legacyExports.events) exports.push('events');

    warnings.push({
      id: warnId(),
      severity: 'warning',
      code: 'LEGACY_DIRECT_EXPORT',
      message: `Plugin "${detection.pluginId}" exports ${exports.join(', ')} at module top level (legacy pattern). ` +
        `Migrate to using the injected context.ui/context.actions/context.events inside setup().`,
      pluginId: detection.pluginId,
      detail: { legacyExports: detection.legacyExports },
      timestamp: new Date(detection.detectedAt).toISOString(),
    });
  }

  // ── 8. Wave 22.13.2: Interactive action invoked but ui.openModal never called ──
  for (const [key, record] of _interactiveActionInvocations.entries()) {
    const elapsed = now - record.invokedAt;

    // Handler threw before calling openModal
    if (record.handlerError) {
      warnings.push({
        id: warnId(),
        severity: 'error',
        code: 'INTERACTIVE_ACTION_HANDLER_ERROR',
        message: `Plugin "${record.pluginId}" action handler threw before calling ui.openModal(): ${record.handlerError}`,
        pluginId: record.pluginId,
        detail: { actionId: record.viewId, error: record.handlerError },
        timestamp: new Date(record.invokedAt).toISOString(),
      });
      continue;
    }

    // Action handler ran but openModal was never called within threshold
    if (!record.openModalCalled && elapsed > INTERACTIVE_NO_MODAL_THRESHOLD_MS) {
      warnings.push({
        id: warnId(),
        severity: 'warning',
        code: 'INTERACTIVE_ACTION_NO_MODAL',
        message: `Plugin "${record.pluginId}" interactive action "${record.viewId ?? 'unknown'}" was invoked ${Math.round(elapsed / 1000)}s ago ` +
          `but ui.openModal() was never called. The action handler may not be invoking ui.openModal() correctly.`,
        pluginId: record.pluginId,
        detail: { actionId: record.viewId, elapsedMs: elapsed, key },
        timestamp: new Date(record.invokedAt).toISOString(),
      });
    }
  }

  return warnings;
}

/**
 * Scan invocation log entries for warning conditions.
 * Returns warnings derived from historical log entries.
 */
export function computeLogWarnings(
  logEntries: UIInvocationLogEntry[],
): InteractionWarning[] {
  const warnings: InteractionWarning[] = [];
  const registeredViewIds = new Set(getRegisteredModalViewIds());

  for (const entry of logEntries) {
    // Warn on modal-open if viewId is not registered
    if (entry.kind === 'modal-open' && entry.detail?.viewId) {
      if (!registeredViewIds.has(entry.detail.viewId)) {
        warnings.push({
          id: warnId(),
          severity: 'warning',
          code: 'LOG_MODAL_UNREGISTERED_VIEW',
          message: `Log: modal-open for viewId="${entry.detail.viewId}" — no component registered.`,
          pluginId: entry.pluginId,
          detail: { viewId: entry.detail.viewId, logEntryId: entry.id },
          timestamp: entry.timestamp,
        });
      }
    }
  }

  return warnings;
}
